/**
 * Shared formatting utilities for consistent display across the application
 */

/**
 * Format salary range for display with period
 */
export const formatSalary = (
  salaryMin?: number | null,
  salaryMax?: number | null,
  currency: string = 'USD',
  notSpecifiedText: string = 'Not Specified',
  salaryPeriod?: string | null,
  t?: (key: string) => string
): string => {
  if (!salaryMin && !salaryMax) return notSpecifiedText;
  
  // Get translated currency if available
  const currencyKey = `currencies.${currency.toLowerCase()}`;
  const displayCurrency = t ? (t(currencyKey) || currency) : currency;
  
  // Format the salary amount
  let salaryText = '';
  if (salaryMin && salaryMax) {
    salaryText = `${displayCurrency} ${salaryMin.toLocaleString()} - ${salaryMax.toLocaleString()}`;
  } else if (salaryMin) {
    salaryText = `From ${displayCurrency} ${salaryMin.toLocaleString()}`;
  } else if (salaryMax) {
    salaryText = `Up to ${displayCurrency} ${salaryMax.toLocaleString()}`;
  } else {
    return notSpecifiedText;
  }
  
  // Add period if provided
  if (salaryPeriod) {
    const periodKey = `salary_periods.per_${salaryPeriod === 'yearly' ? 'year' : salaryPeriod === 'monthly' ? 'month' : 'hour'}`;
    const periodText = t ? t(periodKey) : (salaryPeriod === 'yearly' ? 'per year' : salaryPeriod === 'monthly' ? 'per month' : 'per hour');
    return `${salaryText} ${periodText}`;
  }
  
  return salaryText;
};

/**
 * Format employment type for display
 */
export const formatEmploymentType = (type: string, t?: (key: string) => string): string => {
  // Try translation first if t function is provided
  if (t) {
    const translated = t(`employment_types.${type.replace('-', '_')}`);
    if (translated && !translated.startsWith('employment_types')) {
      return translated;
    }
  }
  
  // Fallback to capitalized version
  return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-');
};
